import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, Subject, takeUntil, tap } from 'rxjs';
import { ApiState } from '../../model/api-state.model';
import { PersistStorageKeys, PersistStorageService } from '../../services/persist-storage.service';
import { AppState } from '../../store';
import * as fromProcess from '../../store/process/process.reducer';
import { TrackingService } from '../../tracking/tracking.service';
import { isEmpty } from '../../utils';
import DiscountCodeValidator from '../../validator/discountCode.validator';

@Component({
  selector: 'app-discount-code',
  templateUrl: './discount-code.component.html',
  styleUrls: ['./discount-code.component.scss'],
})
export class DiscountCodeComponent implements OnInit, OnDestroy {
  state$ = this.store.select(fromProcess.selectOfferDiscountCodeState);
  error$ = this.store.select(fromProcess.selectOfferDiscountCodeError);
  discountCode$ = this.store.select(fromProcess.selectOfferDiscountCode);
  isValid$ = this.store.select(fromProcess.selectOfferDiscountCodeIsValid);
  persistedDiscountCode = this.persistStorage.getString(PersistStorageKeys.DiscountCode);

  ngDestroy$ = new Subject<void>();

  promoCheckbox = false;

  formGroup = new FormGroup({
    discountCode: new FormControl('', {
      validators: [Validators.required, DiscountCodeValidator],
    }),
  });

  protected readonly apiState = ApiState;

  constructor(private store: Store<AppState>, private persistStorage: PersistStorageService, private trackingService: TrackingService) {}

  ngOnInit(): void {
    this.setDiscountCode();
    this.setErrors();
    this.handleInitDiscountCode();
    this.implementTracking();
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }

  handleSubmit(): void {
    if (this.formGroup.valid) {
      this.store.dispatch(fromProcess.actions.fetchDiscountCodeStart({ discountCode: this.formGroup.value.discountCode ?? '' }));
    }
  }

  removeDiscountCode(): void {
    this.persistedDiscountCode = null;
    this.persistStorage.removeItem(PersistStorageKeys.DiscountCode, 'session');
    this.store.dispatch(fromProcess.actions.removeDiscountCode());

    this.formGroup.controls.discountCode.setValue('');
    this.formGroup.controls.discountCode.setErrors(null);
    this.formGroup.markAsUntouched();
  }

  promoCheckboxClicked(): void {
    if (this.promoCheckbox) {
      this.removeDiscountCode();
    }
  }

  private handleInitDiscountCode(): void {
    this.isValid$
      .pipe(
        distinctUntilChanged(),
        tap((isValid) => {
          if (isValid) {
            return;
          }

          this.persistedDiscountCode = this.persistStorage.getString(PersistStorageKeys.DiscountCode);
          if (!this.persistedDiscountCode) {
            return;
          }

          this.store.dispatch(fromProcess.actions.fetchDiscountCodeStart({ discountCode: this.persistedDiscountCode ?? '' }));
        }),
      )
      .subscribe();
  }

  private setDiscountCode(): void {
    if (this.persistedDiscountCode) {
      this.promoCheckbox = true;

      this.formGroup.patchValue({
        discountCode: this.persistedDiscountCode || '',
      });
    }

    this.discountCode$
      .pipe(
        takeUntil(this.ngDestroy$),
        filter((value) => !isEmpty(value)),
      )
      .subscribe((discountCode) => {
        if (discountCode) {
          this.promoCheckbox = true;
        }

        this.formGroup.patchValue({
          discountCode: discountCode || '',
        });
      });
  }

  private setErrors(): void {
    combineLatest([this.state$, this.isValid$])
      .pipe(
        takeUntil(this.ngDestroy$),
        filter(([state]) => state === ApiState.success || state === ApiState.error),
      )
      .subscribe(([_, isValid]) => {
        if (isValid) {
          this.formGroup.controls.discountCode.setErrors(null);

          return;
        }

        this.formGroup.controls.discountCode.setErrors({
          invalidCode: true,
        });
      });
  }

  private implementTracking(): void {
    combineLatest([this.isValid$, this.discountCode$])
      .pipe(
        takeUntil(this.ngDestroy$),
        filter(([isValid]) => isValid),
      )
      .subscribe(([isValid, discountCode]) => {
        this.trackingService.promoCode(isValid);
        this.trackingService.specialOffer(discountCode);
      });
  }
}
